// Live-Zusammenarbeit über Yjs. Das Projekt liegt als JSON-Snapshot in einer Y.Map;
// jede lokale Änderung wird eingetragen, entfernte Änderungen ersetzen den lokalen Zustand.
// Für den Start reicht das (Konflikte: letzte Änderung gewinnt pro Projekt-Version).
// Ausbaustufe: Spuren und Clips als eigene Y.Maps, damit gleichzeitige Änderungen an
// verschiedenen Spuren ohne Verlust zusammengeführt werden (siehe docs/ARCHITEKTUR.md).
import * as Y from 'yjs';
import { WebsocketProvider } from 'y-websocket';
import type { Presence, Project } from '@srs/shared';
import { projectListeners, useStudio } from '../state/store.ts';
import { wsUrl, api } from '../api/client.ts';
import { engine } from '../audio/engine.ts';

let provider: WebsocketProvider | null = null;
let doc: Y.Doc | null = null;
let unlisten: (() => void) | null = null;

export function joinRoom(room: string) {
  leaveRoom();
  const st = useStudio.getState();
  if (!st.token) throw new Error('Nicht angemeldet');
  doc = new Y.Doc();
  provider = new WebsocketProvider(wsUrl('/yjs'), room, doc, { params: { token: st.token } });
  const map = doc.getMap<string>('project');

  provider.on('status', (e: { status: string }) => useStudio.getState().setOnline({ collab: e.status === 'connected' }));
  provider.on('sync', (synced: boolean) => {
    if (!synced) return;
    const remote = map.get('json');
    if (remote) applyRemote(JSON.parse(remote));
    else map.set('json', JSON.stringify(useStudio.getState().project));
  });
  map.observe((e) => {
    if (e.transaction.local) return;
    const json = map.get('json');
    if (json) applyRemote(JSON.parse(json));
  });

  const listener = (p: Project, remote: boolean) => { if (!remote && doc) doc.transact(() => map.set('json', JSON.stringify(p))); };
  projectListeners.add(listener);
  unlisten = () => projectListeners.delete(listener);

  // Anwesenheit: wer ist da, wo steht der Cursor, was tut er gerade
  const aw = provider.awareness;
  const me: Presence = { user: { id: st.user.id, name: st.user.name, color: st.user.color, city: st.user.city }, view: st.view };
  aw.setLocalState(me);
  aw.on('change', () => {
    const peers = [...aw.getStates().entries()].filter(([id]) => id !== aw.clientID).map(([, s]) => s as Presence).filter((s) => s?.user);
    useStudio.getState().setPeers(peers);
  });
}

export function updatePresence(p: Partial<Presence>) {
  if (!provider) return;
  const cur = provider.awareness.getLocalState() as Presence | null;
  if (cur) provider.awareness.setLocalState({ ...cur, ...p });
}

function applyRemote(p: Project) {
  const cur = useStudio.getState().project;
  if (p.id === cur.id && p.version < cur.version && p.updatedAt < cur.updatedAt) return;
  useStudio.getState().replaceProject(p, true);
  // Audio entfernter Takes nachladen
  for (const c of p.clips) if (c.kind === 'audio' && c.sourceId && !engine.buffers.has(c.sourceId) && !c.sourceId.startsWith('src-')) {
    api.media(c.sourceId).then((d) => engine.decode(c.sourceId!, d)).catch(() => undefined);
  }
}

export function leaveRoom() {
  unlisten?.(); unlisten = null;
  provider?.destroy(); provider = null;
  doc?.destroy(); doc = null;
  useStudio.getState().setOnline({ collab: false });
  useStudio.getState().setPeers([]);
}
